import React, { useEffect, useMemo, useRef, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { UserProvider } from './Integrations/UserContext'; // Import the UserProvider
import { TrainingProvider } from "./Integrations/TrainingContext";
import TrainingOverlay from "./Integrations/TrainingOverlay";
import Header from './Integrations/Headers/Header';
import HomePage from './pages/HomePage';
import Play from './pages/Play';
import NotFoundPage from './pages/NotFoundPage';
import Login from './pages/Login';
import EndgamePositions from './pages/EndgamePositions';
import Puzzles from './pages/Puzzles';
import SignUp from './pages/SignUp';
import './firebase-config'; // Ensure Firebase is initialized here if not already done elsewhere
import LoadGame from './pages/LoadGame';
import Settings from './pages/Settings'; // Adjust the import path as needed
import { ThemeProvider } from './Integrations/ThemesContext'; // Import ThemeProvider
import { useNavigate } from 'react-router-dom';
import ThemeApplier from './Integrations/ThemeApplier';
import About from './pages/About';
import BlindfoldPuzzles from './pages/BlindfoldPuzzles';
import CalculationPuzzles from './pages/CalculationPuzzles';
import MoveTrainer from './pages/MoveTrainer';
import FindFork from './pages/FindFork';
import CoordinateTrainer from './pages/CoordinateTrainer';
import TimedPuzzles from './pages/TimedPuzzles';
import ClubPage from './pages/ClubPage';
import VoteChess from './pages/VoteChess';
import PrivacyPolicy from './pages/Help/PrivacyPolicy';
import TrainChessOpenings from './pages/TrainChessOpenings';
import ManageSubscription from './pages/ManageSubscription';
import GameAnalysis from './pages/GameAnalysis';
import Analyze from './pages/Analyze';
import { GameStateProvider } from "./Integrations/GameStateContext";
import Test from './Integrations/Test';
import Train from './pages/Train';
import PlayGamesOptions from './pages/PlayGamesOptions'; 
import TheExchangeLandingPage from './pages/TheExchange/linkpage';
import LinkTree from './pages/LinkTree';
// Import the new help components
import Help from './pages/Help/Help'; // Main Help Page
import CalculationPuzzlesHelp from './pages/Help/calculationpuzzles'; // Specific Help Page for Calculation Puzzles
import FindTheForkHelp from './pages/Help/findthefork'; // Specific Help Page for Calculation Puzzles
import CoordinateTrainerHelp from './pages/Help/coordinatetrainer'; // Specific Help Page for Calculation Puzzles
import PlayBlindfoldHelp from './pages/Help/playblindfold'; // Specific Help Page for Calculation Puzzles
import PuzzlesHelp from './pages/Help/puzzles'; // Specific Help Page for Calculation Puzzles
import BlindfoldPuzzlesHelp from './pages/Help/blindfoldpuzzles'; // Specific Help Page for Calculation Puzzles
import ContactPage from './pages/Help/contact'; // Specific Help Page for Calculation Puzzles
import AlgebraicNotation from './pages/Help/algebraicnotation'; // Specific Help Page for Calculation Puzzles
import RatingMetrics from './pages/Help/ratingmetrics'; // Specific Help Page for Calculation Puzzles
import Welcome from './pages/Help/Welcome'; // Specific Help Page for Calculation Puzzles
import GameAnalysisHelp from './pages/Help/gameanalysis'; // Specific Help Page for Calculation Puzzles
import EndgamesHelp from './pages/Help/endgames'; // Specific Help Page for Calculation Puzzles
import CoordinateTrainerType from './pages/CoordinateTrainerType';
import SupportPage from './pages/TheExchange/support';
import TCSupportPage from './pages/TrainChessApp/support';
import PrivacyPolicyPage from './pages/TheExchange/privacypolicy';
import TCPrivacyPolicyPage from './pages/TrainChessApp/privacypolicy';
import TerminateAccount from './pages/TheExchange/terminateaccount';
import ChildSafety from './pages/TheExchange/childsafety';
import Minigames from './pages/Minigames';
import StockfishBridge from './pages/StockfishBridge';
import ChapterTemplate from './Integrations/ChapterTemplate';
import { ADS } from './components/Ads';

// ---------------- Manual Ad (Global) ----------------
// Config: where and how often to show the ad
const AD_CONFIG = {
  delayMs: 5000,                 // show immediately; close is locked by countdown
  cooldownHours: 125,          // don't show again for this many hours after dismiss
  countdownSeconds: 5,        // user must wait this many seconds before closing
  includePaths: [             // only show on these routes (exact or prefix)
    '/train',
    '/play',
    '/puzzles',
    '/endgames',
    '/openingtrainer',
    '/coordinatetrainer',
    '/calculationpuzzles',
    '/blindfoldpuzzles',
    '/findthefork',
    '/gameanalysis',
    '/analyze',
    '/howtotrainchess',
    '/minigames'
  ],
  excludeExact: [             // never show on these exact routes
    '/',
    '/login',
    '/signup',
    '/settings',
    '/theexchange',
    '/theexchange/support',
    '/theexchange/privacypolicy',
    '/theexchange/terminate',
    '/theexchange/childsafety',
    '/theexchange/stockfishbridge',
  ],
  storageKey: 'ad.exchange.cooldownAt'
};

function pathMatches(pathname) {
  if (AD_CONFIG.excludeExact.includes(pathname)) return false;
  // show if the current path starts with any includePath
  return AD_CONFIG.includePaths.some(p => pathname === p || pathname.startsWith(p + '/'));
}

// Minimal, dependency-free modal for the manual ad
function ManualAdModal({ open, onClose, children }) {
  if (!open) return null;
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 9999
    }}>
      <div style={{
        width: 'min(520px, 92vw)', background: 'var(--bg, #111)',
        color: 'var(--text, #fff)', borderRadius: 12, padding: '20px 20px 16px',
        boxShadow: '0 12px 40px rgba(0,0,0,0.35)'
      }}>
        {children}
      </div>
    </div>
  );
}


function AdRenderer({ adKey, ...props }) {
  const Comp = ADS[adKey];
  if (!Comp) return null;
  return <Comp {...props} />;
}

function GlobalAdController({ adKey = 'exchange' }) {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [canClose, setCanClose] = useState(false);
  const delayTimerRef = useRef(null);
  const tickRef = useRef(null);

  const dismissForCooldown = () => {
    // Set cooldown
    const until = Date.now() + AD_CONFIG.cooldownHours * 3600 * 1000;
    try { localStorage.setItem(AD_CONFIG.storageKey, String(until)); } catch {}

    // Clear timers and close
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
    if (delayTimerRef.current) { clearTimeout(delayTimerRef.current); delayTimerRef.current = null; }
    setOpen(false);
  };

  useEffect(() => {
    // Always clear any previous timers when path changes
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
    if (delayTimerRef.current) { clearTimeout(delayTimerRef.current); delayTimerRef.current = null; }

    // If this path shouldn't show ads, ensure closed and exit
    if (!pathMatches(location.pathname)) {
      setOpen(false);
      return;
    }

    // Respect cooldown
    try {
      const raw = localStorage.getItem(AD_CONFIG.storageKey);
      if (raw && Number(raw) > Date.now()) {
        setOpen(false);
        return;
      }
    } catch {}

    // Schedule the ad to open after the configured delay
    const delay = Number(AD_CONFIG.delayMs) || 0;
    delayTimerRef.current = setTimeout(() => {
      // Open and start the close-unlock countdown
      setOpen(true);
      setCanClose(false);
      setSecondsLeft(AD_CONFIG.countdownSeconds);

      tickRef.current = setInterval(() => {
        setSecondsLeft(prev => {
          const next = prev - 1;
          if (next <= 0) {
            if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
            setCanClose(true);
            return 0;
          }
          return next;
        });
      }, 1000);
    }, delay);

    // Cleanup on unmount or path change
    return () => {
      if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
      if (delayTimerRef.current) { clearTimeout(delayTimerRef.current); delayTimerRef.current = null; }
    };
  }, [location.pathname]);

  return (
    <ManualAdModal open={open} onClose={() => setOpen(false)}>
      <AdRenderer adKey={adKey} onDismiss={dismissForCooldown} secondsLeft={secondsLeft} canClose={canClose} />
    </ManualAdModal>
  );
}
// ---------------- End Manual Ad ----------------

function App() {
  return (
    <Router>
      <UserProvider> {/* Wrap the routes with UserProvider */}
        <ThemeProvider> {/* Additionally, wrap the routes with ThemeProvider */}
          <GameStateProvider>
            <TrainingProvider>
              <ThemeApplier />
              <RoutesWithHeader />
              <TrainingOverlay />
            </TrainingProvider>
          </GameStateProvider>
        </ThemeProvider>
      </UserProvider>
    </Router>
  );
}

const RoutesWithHeader = () => {
  let location = useLocation();

  return (
    <>
      {location.pathname !== '/' && <Header />}
      <GlobalAdController adKey="exchange" />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/train" element={<Train />} />
        <Route path="/playgames" element={<PlayGamesOptions />} />
        <Route path="/play" element={<PlayWrapper />} />
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<SignUp />} />
        <Route path="/loadgame" element={<LoadGame />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/endgames" element={<EndgamePositions />} />
        <Route path="/blindfoldpuzzles" element={<BlindfoldPuzzles />} />
        <Route path="/calculationpuzzles" element={<CalculationPuzzles />} />
        <Route path="/puzzles" element={<TimedPuzzles />} />
        <Route path="/findthefork" element={<FindFork />} />
        <Route path="/coordinatetrainer" element={<CoordinateTrainer />} />
        <Route path="/coordinatetrainer2" element={<CoordinateTrainerType />} />
        <Route path="/openingtrainer" element={<MoveTrainer />} />
        <Route path="/openingtrainer/browse" element={<TrainChessOpenings />} />
        <Route path="/teamchess" element={<VoteChess />} />
        <Route path="/gameanalysis" element={<GameAnalysis />} />
        <Route path="/analyze" element={<Analyze />} />
        <Route path="/managesubscription" element={<ManageSubscription />} />
        <Route path="/club" element={<ClubPage />} />
        <Route path="/minigames" element={<Minigames />} />
        <Route path="/howtotrainchess/:chapterId" element={<ChapterTemplate />} />
        <Route path="/about" element={<About />} />
        <Route path="/linktree" element={<LinkTree />} />
        <Route path="/help" element={<Help />} /> {/* Main Help Page */}
        <Route path="/privacypolicy" element={<PrivacyPolicy />} /> {/* Calculation Puzzles Help */}
        <Route path="/calculationpuzzles/help" element={<CalculationPuzzlesHelp />} /> {/* Calculation Puzzles Help */}
        <Route path="/findthefork/help" element={<FindTheForkHelp />} /> {/* Calculation Puzzles Help */}
        <Route path="/coordinatetrainer/help" element={<CoordinateTrainerHelp />} /> {/* Calculation Puzzles Help */}
        <Route path="/playblindfold/help" element={<PlayBlindfoldHelp />} /> {/* Calculation Puzzles Help */}
        <Route path="/puzzles/help" element={<PuzzlesHelp />} /> {/* Calculation Puzzles Help */}
        <Route path="/blindfoldpuzzles/help" element={<BlindfoldPuzzlesHelp />} /> {/* Calculation Puzzles Help */}
        <Route path="/algebraicnotation" element={<AlgebraicNotation />} /> {/* Calculation Puzzles Help */}
        <Route path="/ratingmetrics" element={<RatingMetrics />} /> {/* Calculation Puzzles Help */}
        <Route path="/gameanalysis/help" element={<GameAnalysisHelp />} /> {/* Calculation Puzzles Help */}
        <Route path="/endgames/help" element={<EndgamesHelp />} /> {/* Calculation Puzzles Help */}
        <Route path="/contact" element={<ContactPage />} /> {/* Calculation Puzzles Help */}
        <Route path="/welcome" element={<Welcome />} /> {/* Calculation Puzzles Help */}
        <Route path="/TCapp/privacypolicy" element={<TCPrivacyPolicyPage />} /> {/* Calculation Puzzles Help */}
        <Route path="/theexchange/support" element={<SupportPage />} /> {/* Calculation Puzzles Help */}
        <Route path="/TCapp/support" element={<TCSupportPage />} /> {/* Calculation Puzzles Help */}
        <Route path="/theexchange/privacypolicy" element={<PrivacyPolicyPage />} /> {/* Calculation Puzzles Help */}
        <Route path="/theexchange" element={<TheExchangeLandingPage />} /> {/* Calculation Puzzles Help */}
        <Route path="/theexchangelink/*" element={<SmartTheExchangeLink />} />
        <Route path="/app/*" element={<SmartTrainChessLink />} />

        <Route path="/theexchange/terminate" element={<TerminateAccount />} /> {/* Calculation Puzzles Help */}
        <Route path="/theexchange/childsafety" element={<ChildSafety />} /> {/* Calculation Puzzles Help */}
        <Route path="/theexchange/stockfishbridge" element={<StockfishBridge />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </>
  );
}

/**
 * Smart deep link handler for /theexchangelink/* routes.
 * - If on iOS: try to open the app via custom scheme; if not installed, fall back to App Store.
 * - Else: render the normal landing page and stay on this page.
 *
 * TODO: Replace APP_STORE_URL with your real App Store URL (with the correct app id).
 */
const APP_STORE_URL = 'https://apps.apple.com/us/app/the-exchange-chess/id6742661001';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.theexchange';

// --- Instagram/UA helpers for SmartTheExchangeLink ---
const isInstagramUA = () => /Instagram/i.test(navigator.userAgent || "");
const isAndroidUA = () => /Android/i.test(navigator.userAgent || "");

function buildAndroidChromeIntentUrl(fullUrl) {
  // intent://host/path?query#Intent;scheme=https;package=com.android.chrome;end
  try {
    const u = new URL(fullUrl);
    const hostAndPath = u.host + u.pathname + (u.search || "") + (u.hash || "");
    return `intent://${hostAndPath}#Intent;scheme=${u.protocol.replace(":", "")};package=com.android.chrome;end`;
  } catch {
    return null;
  }
}

const SmartTrainChessLink = () => {
  const location = useLocation();
  const path = `${location.pathname}${location.search}${location.hash}`;
  const nativeUrl = `trainchess://${path.replace(/^\//, '')}`;
  const isInstagram = useMemo(() => isInstagramUA(), []);

  if (isInstagram) {
    return (
      <div className="trainchess-link-page">
        <h2>Open in Safari to continue</h2>
        <p>Instagram blocks app links. Open this page in Safari, then Train Chess should open normally.</p>
      </div>
    );
  }

  return (
    <div className="trainchess-link-page">
      <h2>Continue to Train Chess</h2>
      <p>If the app did not open automatically, tap below.</p>
      <a className="trainchess-link-button" href={nativeUrl}>Open Train Chess</a>
    </div>
  );
};

const SmartTheExchangeLink = () => {
  const isIOS = useMemo(() => /iPhone|iPad|iPod/i.test(navigator.userAgent), []);
  const isInstagram = useMemo(() => isInstagramUA(), []);
  const isAndroid = useMemo(() => isAndroidUA(), []);
  const playStoreUrl = useMemo(() => PLAY_STORE_URL, []);
  const currentUrl = typeof window !== 'undefined' ? window.location.href : '';
  const androidIntentUrl = useMemo(() => buildAndroidChromeIntentUrl(currentUrl), [currentUrl]);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(currentUrl);
      alert('Link copied! You can paste it in your browser to continue.');
    } catch {
      alert('Copy failed. You can long‑press the URL bar and paste manually.');
    }
  };

  const handleOpenInBrowser = () => {
    try {
      window.open(currentUrl, '_blank', 'noopener,noreferrer');
    } catch {}
  };

  useEffect(() => {
    // On iOS (and not inside Instagram), we do not auto-redirect anywhere.
    // We simply render clear next steps in the UI below.
    if (!isIOS || isInstagram) return;
  }, [isIOS, isInstagram]);

  // If opened inside Instagram's in-app browser, provide an explicit way to open in a real browser
  if (isInstagram) {
    if (isAndroid) {
      return (
        <div style={{ padding: '2rem', maxWidth: 520, margin: '0 auto', textAlign: 'center' }}>
          <h2>Open in your browser</h2>
          <p>Instagram prevents external links. Tap the button below to open this page in Chrome.</p>
          {androidIntentUrl && (
            <a
              href={androidIntentUrl}
              style={{ display: 'inline-block', padding: '12px 16px', borderRadius: 8, textDecoration: 'none', border: '1px solid #ccc', marginTop: 12 }}
              rel="noopener noreferrer"
            >
              Open in Chrome
            </a>
          )}
          <p style={{ marginTop: 16, fontSize: 14, opacity: 0.8 }}>
            If nothing happens, tap the ⋯ menu and choose <em>Open in browser</em>.
          </p>
        </div>
      );
    }

    // iOS inside Instagram — cannot programmatically open Safari; provide instructions + Copy link
    return (
      <div style={{ padding: '2rem', maxWidth: 520, margin: '0 auto', textAlign: 'center' }}>
        <h2>Open in Safari to continue</h2>
        <p>Instagram prevents external links. To continue you must:</p>
        <ol style={{ textAlign: 'left', margin: '0 auto', maxWidth: 420 }}>
          <li>Tap the <strong>⋯</strong> at the top-right.</li>
          <li>Select <strong>Open in system browser</strong> (Safari).</li>
        </ol>
        <p style={{ marginTop: 12, fontSize: 14, opacity: 0.8 }}>
          Once in your browser, the link will work normally.
        </p>
        <button
          onClick={handleCopyLink}
          style={{ marginTop: 16, padding: '10px 14px', borderRadius: 8, border: '1px solid #ccc', background: '#fff', cursor: 'pointer' }}
        >
          Copy Link
        </button>
      </div>
    );
  }

  if (isIOS) {
    return (
      <div style={{ padding: '2rem', maxWidth: 560, margin: '0 auto', textAlign: 'center' }}>
        <h2>Continue to The Exchange</h2>
        <p style={{ marginTop: 8 }}>
          If you already have the app installed, it should have opened. If it didn’t, follow the steps below:
        </p>
        <ol style={{ textAlign: 'left', margin: '16px auto', maxWidth: 460, lineHeight: 1.5 }}>
          <li><strong>Open in your browser:</strong> if you are opening the link from within another app (e.g. Reddit or Instagram) you may need to click an 'open in browser' option.</li>
          <li><strong>Retry the link:</strong> if that doesn’t work, reload the page or copy this link and paste it into a trusted browser.</li>
          <li><strong>Don’t have the app?</strong> install it from the App Store, make an account and then return to this link.</li>
        </ol>

        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'center', marginTop: 8 }}>
          <button
            onClick={handleOpenInBrowser}
            style={{ padding: '10px 14px', borderRadius: 8, border: '1px solid #ccc', background: '#fff', cursor: 'pointer' }}
          >
            Reopen Page
          </button>

          <button
            onClick={handleCopyLink}
            style={{ padding: '10px 14px', borderRadius: 8, border: '1px solid #ccc', background: '#fff', cursor: 'pointer' }}
          >
            Copy Link
          </button>

          <a
            href={APP_STORE_URL}
            style={{ display: 'inline-block', padding: '10px 14px', borderRadius: 8, textDecoration: 'none', border: '1px solid #ccc' }}
            rel="noopener noreferrer"
          >
            Get it on the App Store
          </a>
        </div>
      </div>
    );
  }

  // Android (not Instagram): show similar guidance + Play Store button
  if (isAndroid) {
    return (
      <div style={{ padding: '2rem', maxWidth: 560, margin: '0 auto', textAlign: 'center' }}>
        <h2>Continue to The Exchange</h2>
        <p style={{ marginTop: 8 }}>
          If you already have the app installed, it should have opened. If it didn’t, follow the steps below:
        </p>
        <ol style={{ textAlign: 'left', margin: '16px auto', maxWidth: 460, lineHeight: 1.5 }}>
          <li><strong>Open in your browser:</strong> if you are opening the link from within another app, use an “Open in browser” option.</li>
          <li><strong>Retry the link:</strong> reload the page or copy this link and paste it into Chrome.</li>
          <li><strong>Don’t have the app?</strong> install it from Google Play, make an account and then return to this link.</li>
        </ol>

        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'center', marginTop: 8 }}>
          <button
            onClick={handleOpenInBrowser}
            style={{ padding: '10px 14px', borderRadius: 8, border: '1px solid #ccc', background: '#fff', cursor: 'pointer' }}
          >
            Reopen Page
          </button>

          <button
            onClick={handleCopyLink}
            style={{ padding: '10px 14px', borderRadius: 8, border: '1px solid #ccc', background: '#fff', cursor: 'pointer' }}
          >
            Copy Link
          </button>

          <a
            href={playStoreUrl}
            style={{ display: 'inline-block', padding: '10px 14px', borderRadius: 8, textDecoration: 'none', border: '1px solid #ccc' }}
            rel="noopener noreferrer"
          >
            Get it on Google Play
          </a>
        </div>
      </div>
    );
  }

  // Non‑iOS: keep users on the landing page
  return <TheExchangeLandingPage />;
};

// Create a wrapper component for Play to inject the navigate function
const PlayWrapper = () => {
  let navigate = useNavigate();
  return <Play navigate={navigate} />;
}

export default App;
